import { visibleTrend, trendEmptyMessage } from '../trendPeriods'
import { PartialPeriods } from './PartialPeriods'
import { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../api/http'
import { useRequestState } from '../useRequestState'
import { flagUnauthorized } from '../api/auth'
import { TENANT } from '../tenant'
import { fetchMarketShareTrend, mergeTrends, FREQ_LABEL, formatRangeLabel, type Freq } from '../landing'
import { displaySegment, segmentApiValue, segmentLabel, type Segment } from '../segments'
import type { Chart } from '../types'
import { formatCU } from '../format'
import { InsightChart } from '../charts/InsightChart'
import './ArticleComparison.css'
import { ArticlePicker, articleLabel as label, articleShortLabel as shortLabel, type Article } from './ArticlePicker'
import { InfoTip } from './InfoTip'
import { kitContentsText, kitInfo } from '../missingLabels'
import { ShareBasisLine } from './ShareBasisLine'

type Comparison = { articles: (Article & { total: number | null; chart: Chart })[]; segment: string }
const base = import.meta.env.VITE_API_URL ?? ''
function params() {
  const q = new URLSearchParams()
  if (TENANT.manufacturer) q.set('manufacturer', TENANT.manufacturer)
  return q
}
function useData<T>(path: string | null, attempt: number) {
  const key = `${path}|${attempt}`
  const [result, setResult] = useRequestState<{ key: string; data?: T; error?: string } | null>(key, null)
  useEffect(() => {
    if (!path) return
    const controller = new AbortController()
    apiFetch(`${base}${path}`, { signal: controller.signal }).then(async response => {
      if (response.status === 401) flagUnauthorized()
      const body = await response.json()
      if (!response.ok) throw new Error(body.detail || 'Article data could not be loaded.')
      if (!controller.signal.aborted) setResult({ key, data: body })
    }).catch(error => { if (!controller.signal.aborted) setResult({ key, error: String(error.message || error) }) })
    return () => controller.abort()
  }, [path, attempt, key])
  return result?.key === key ? result : null
}

function useSoldSet(sold: string[] | undefined) {
  return useMemo(() => sold ? new Set(sold) : null, [sold])
}

// MPD-135: a kit with reported contents gets an ⓘ with its contents and parts; MPD-141 F-L1: the
// comparison articles too, not only the primary.
const kitTip = (a: Article) => a.kit && kitContentsText(a.kit_contents)
  ? <InfoTip label="About this kit" size="small">{kitInfo(a.kit_contents)}</InfoTip> : null

export function ArticleComparison({ segment, from, to, freq }: { segment: Segment; from: string; to: string; freq: Freq }) {
  const [attempt, setAttempt] = useState(0)
  const [showPartial, setShowPartial] = useState(false)
  const [primaryChoice, setPrimaryChoice] = useState('')
  const [peers, setPeers] = useState<Article[]>([])
  const ownParams = params()
  const seg = segmentApiValue(segment)
  if (seg) ownParams.set('segment', seg)
  const own = useData<{ articles: Article[] }>(`/api/dashboard/articles?${ownParams}`, attempt)
  const [ownCache, setOwnCache] = useState<{ scope: string; articles: Article[] } | null>(null)
  const scope = ownParams.toString()
  useEffect(() => { if (own?.data) setOwnCache({ scope, articles: own.data.articles }) }, [own?.data, scope])
  const articles = own?.data?.articles ?? (ownCache?.scope === scope ? ownCache.articles : [])
  const primary = articles.find(a => a.sku === primaryChoice)
  const marketParams = params()
  if (primary) marketParams.set('primary', primary.sku)
  if (seg) marketParams.set('segment', seg)
  const market = useData<{ articles: Article[] }>(primary ? `/api/dashboard/articles?${marketParams}` : null, attempt)
  const options = market?.data?.articles ?? []
  const validPeers = peers.map(a => options.find(option => option.sku === a.sku) ?? a)
    .filter(a => a.sku !== primary?.sku && a.segment === primary?.segment
      && (!market?.data || options.some(option => option.sku === a.sku)))
  // Commit removal once a new catalogue has arrived; never erase selections on a failed request.
  useEffect(() => {
    if (own?.data) setPrimaryChoice(current => articles.some(a => a.sku === current) ? current : '')
  }, [own?.data])
  useEffect(() => {
    if (market?.data) setPeers(current => current.flatMap(a => {
      const latest = options.find(option => option.sku === a.sku && option.sku !== primary?.sku)
      return latest ? [latest] : []
    }))
  }, [market?.data, primary?.sku])
  const query = params()
  query.set('primary', primary?.sku ?? '')
  query.set('date_from', from); query.set('date_to', to); query.set('granularity', freq)
  validPeers.forEach(a => query.append('compare', a.sku))
  const result = useData<Comparison>(primary && market?.data ? `/api/dashboard/article-comparison?${query}` : null, attempt)
  // MP-OS-208: tag suggestions without sales in the period; same scope and parameters as each list.
  const period = new URLSearchParams({ date_from: from, date_to: to })
  const ownSales = useData<{ sold?: string[] }>(`/api/dashboard/article-sales?${ownParams}&${period}`, attempt)
  const marketSales = useData<{ sold?: string[] }>(primary ? `/api/dashboard/article-sales?${marketParams}&${period}` : null, attempt)
  const ownSold = useSoldSet(ownSales?.data?.sold)
  const marketSold = useSoldSet(marketSales?.data?.sold)
  const shareKey = `${primary?.sku}|${from}|${to}|${freq}|${attempt}`
  const [share, setShare] = useRequestState<{ key: string; chart?: Chart | null; error?: string } | null>(shareKey, null)
  useEffect(() => {
    if (!primary) return
    let cancelled = false
    // MPD-81 S3: send the supplier scope, so an admin's "View as supplier" gets the supplier's
    // narrowed denominator (the server forces it for a real supplier).
    fetchMarketShareTrend({ sku: primary.sku, from, to, freq, ...(TENANT.manufacturer ? { manufacturer: TENANT.manufacturer } : {}) }).then(chart => {
      if (!cancelled) setShare({ key: shareKey, chart })
    }).catch(() => { if (!cancelled) setShare({ key: shareKey, error: 'Could not load the primary article’s market share.' }) })
    return () => { cancelled = true }
  }, [shareKey])
  const currentShare = share?.key === shareKey ? share : null
  const error = own?.error || market?.error || result?.error || currentShare?.error
  const selected = result?.data?.articles ?? []
  const tooltipLabels = Object.fromEntries(selected.map(a => [shortLabel(a), label(a)]))
  const rawChart = result?.data ? mergeTrends(selected.map(a => ({ name: shortLabel(a), chart: a.chart }))) : null
  const partials = rawChart?.data.filter(point => point.period_kind === 'partial') ?? []
  const chart = visibleTrend(rawChart, showPartial)
  const totals: Chart = { type: 'barH', unit: 'CU', ref: null, series: null,
    data: selected.filter(a => a.total !== null).map(a => ({ name: shortLabel(a), value: a.total })) }

  return <section className="article-comparison" aria-label="Article comparison">
    <h2>Article comparison</h2>
    <p className="article-period-caption">{formatRangeLabel(from, to)} · Group by: {FREQ_LABEL[freq]} · {primary ? `Comparison segment: ${displaySegment(primary.segment)}` : `Available segments: ${segmentLabel(segment)}`}
      <InfoTip label="About the article comparison" size="small">Article comparison follows the selected segment and dates and uses its own article selection.</InfoTip></p>
    <div className="article-pickers">
      <fieldset><legend>Your primary article</legend>
        <ArticlePicker key={`primary-${scope}`} label="Search primary article" articles={articles} sold={ownSold} selected={primary} disabled={!own?.data}
          onSelect={a => { if (a.segment !== primary?.segment) setPeers([]); setPrimaryChoice(a.sku) }} />
        {primary && <p>Selected: {label(primary)}{kitTip(primary)} <button onClick={() => { setPrimaryChoice(''); setPeers([]) }}>Clear primary article</button></p>}
        {own?.data && !articles.length && <p>No own articles in this segment.</p>}
      </fieldset>
      <fieldset><legend>Compare article</legend>
        <ArticlePicker key={`compare-${scope}-${primary?.segment}`} label="Search comparison article"
          articles={options.filter(a => a.sku !== primary?.sku && !validPeers.some(peer => peer.sku === a.sku))} sold={marketSold}
          disabled={!own?.data || !market?.data || validPeers.length >= 2}
          onSelect={a => setPeers(current => current.some(peer => peer.sku === a.sku) || current.length >= 2 ? current : [...current, a])} />
        <p role="status">{validPeers.length} of 2 comparison articles selected{validPeers.length >= 2 ? ' — remove an article to choose another.' : '.'}</p>
        {primary && <p>Own and competitor products · {displaySegment(primary.segment)}</p>}
      </fieldset>
    </div>
    <div className="article-selected">{validPeers.map(a => <span key={a.sku}><button onClick={() => setPeers(current => current.filter(peer => peer.sku !== a.sku))}>Remove {label(a)} ×</button>{kitTip(a)}</span>)}</div>
    {error && <p role="alert">{error} <button onClick={() => setAttempt(n => n + 1)}>Retry article data</button></p>}
    {!own && <p role="status">Loading articles…</p>}
    {own?.data && articles.length > 0 && !primary && <p role="status">Choose a primary article to see sales and market share, then add up to two comparison articles.</p>}
    {primary && !market && <p role="status">Loading comparison choices…</p>}
    {primary && <>
    <h3>Total CU · selected date range</h3><p>{from} – {to} · includes partial periods</p>
    {result?.data && <ul className="article-totals">{result.data.articles.map(a => <li key={a.sku}>{shortLabel(a)}: <strong>{a.total === null ? 'No sales observations' : formatCU(a.total)}</strong></li>)}</ul>}
    {totals.data.length > 0 && <InsightChart chart={totals} animate={false} tooltipLabels={tooltipLabels} />}
    {freq !== 'day' && <PartialPeriods checked={showPartial} onChange={setShowPartial} />}
    <div className="article-trend-grid">
    {/* MPD-132: each section is a column with its plot pushed to the bottom (.article-trend-plot),
        so a two-line title or the share basis line on one side never moves that plot below the other. */}
    <section aria-label="Article CU trend"><h3>CU sales trend · per article</h3>
    <div className="article-trend-plot">
    {chart ? <InsightChart chart={chart} animate={false} tooltipLabels={tooltipLabels} emptyMessage={partials.length && !showPartial ? "No complete periods in this range. Enable Show partial periods to see the available observations." : "No sales observations for the selected articles and dates."} /> : primary && !result?.error ? <p>{result?.data ? 'No sales observations for the selected articles and dates.' : 'Loading article trends…'}</p> : null}
    </div>
    </section>
    <section aria-label="Primary article market share">
    <h3>Market share · primary article{primary ? ` · ${displaySegment(primary.segment)}` : ''}</h3>
    {currentShare?.chart?.share_basis && <ShareBasisLine basis={currentShare.chart.share_basis} label="About the primary article’s share" />}
    <div className="article-trend-plot">
    {currentShare?.chart ? <InsightChart chart={visibleTrend(currentShare.chart, showPartial)!} emptyMessage={trendEmptyMessage(currentShare.chart, showPartial, "No share data in this range.")} animate={false} /> : primary && !currentShare?.error ? <p>{currentShare ? 'No share data for this article.' : 'Loading market share…'}</p> : null}
    </div>
    </section>
    </div>
    </>}
  </section>
}
